export class GrantKeyLeaseRegistry {
    namespace;
    onFinalRelease;
    leases = new Map();
    locks = webLockManager();
    retainAutomaticallyRetiredKeys = this.locks === null;
    constructor(namespace, onFinalRelease) {
        this.namespace = namespace;
        this.onFinalRelease = onFinalRelease;
    }
    async acquire(collectionId, keyHandle, signal) {
        const releaseLocal = this.acquireLocal(collectionId, keyHandle);
        if (!this.locks)
            return releaseLocal;
        try {
            const releaseWeb = await acquireSharedWebLock(this.locks, this.lockName(collectionId, keyHandle), signal);
            return once(() => {
                releaseWeb();
                releaseLocal();
            });
        }
        catch (error) {
            releaseLocal();
            throw error;
        }
    }
    deleteExclusive(collectionId, keyHandle, action) {
        if (this.retainAutomaticallyRetiredKeys)
            return;
        if (this.locks) {
            void this.locks.request(this.lockName(collectionId, keyHandle), { mode: "exclusive" }, action).catch(() => undefined);
        }
        else if (!this.has(collectionId, keyHandle)) {
            void Promise.resolve().then(action).catch(() => undefined);
        }
    }
    has(collectionId, keyHandle) {
        return this.leases.get(collectionId)?.has(keyHandle) ?? false;
    }
    handles(collectionId) {
        return this.leases.get(collectionId)?.keys() ?? [];
    }
    acquireLocal(collectionId, keyHandle) {
        const collection = this.leases.get(collectionId) ?? new Map();
        collection.set(keyHandle, (collection.get(keyHandle) ?? 0) + 1);
        this.leases.set(collectionId, collection);
        return once(() => {
            const remaining = (collection.get(keyHandle) ?? 1) - 1;
            if (remaining > 0)
                collection.set(keyHandle, remaining);
            else {
                collection.delete(keyHandle);
                if (collection.size === 0)
                    this.leases.delete(collectionId);
                this.onFinalRelease(collectionId, keyHandle);
            }
        });
    }
    lockName(collectionId, keyHandle) {
        return `mdbase-connect:grant-key:${this.namespace}:${collectionId}:${keyHandle}`;
    }
}
export class GrantKeyLeaseSet {
    collectionId;
    acquire;
    releases = new Map();
    constructor(collectionId, acquire) {
        this.collectionId = collectionId;
        this.acquire = acquire;
    }
    async retain(tokenOrHandle, signal) {
        const handle = typeof tokenOrHandle === "string" ? tokenOrHandle : tokenOrHandle?.keyHandle;
        if (!handle)
            return;
        let lease = this.releases.get(handle);
        if (!lease) {
            lease = this.acquire(this.collectionId, handle, signal);
            this.releases.set(handle, lease);
        }
        try {
            await lease;
        }
        catch (error) {
            if (this.releases.get(handle) === lease)
                this.releases.delete(handle);
            this.release();
            throw error;
        }
    }
    release() {
        for (const lease of this.releases.values())
            void lease.then((release) => release());
        this.releases.clear();
    }
}
export async function retainCurrentGrantToken(readCurrent, leases, signal) {
    while (true) {
        const snapshot = readCurrent();
        if (!snapshot)
            return null;
        await leases.retain(snapshot, signal);
        const current = readCurrent();
        if (!current)
            return null;
        if (current.keyHandle === snapshot.keyHandle)
            return current;
    }
}
function webLockManager() {
    const locks = typeof navigator === "undefined"
        ? undefined
        : navigator.locks;
    if (locks)
        return locks;
    const browser = typeof window !== "undefined"
        || typeof globalThis.importScripts === "function";
    return browser ? null : undefined;
}
async function acquireSharedWebLock(locks, name, signal) {
    let acquired;
    let rejectAcquisition;
    let release;
    const ready = new Promise((resolve, reject) => {
        acquired = resolve;
        rejectAcquisition = reject;
    });
    const held = new Promise((resolve) => { release = resolve; });
    const request = locks.request(name, { mode: "shared", ...(signal ? { signal } : {}) }, async () => {
        acquired();
        await held;
    });
    void request.catch(rejectAcquisition);
    await ready;
    return once(release);
}
function once(action) {
    let called = false;
    return () => {
        if (called)
            return;
        called = true;
        action();
    };
}
